import { UserButton, useUser } from '@clerk/react';
import { type ReactNode, useEffect, useState } from 'react';
import { useDashboard, useProject } from '@/api/hooks';
import { Icon } from '../icon/Icon';
import { Btn } from '../primitives/Btn/Btn';
import { Pill } from '../primitives/Pill/Pill';
import { CommandPalette } from './CommandPalette';
import styles from './Topbar.module.css';

interface TopbarProps {
  right?: ReactNode;
  onMenuClick?: () => void;
}

// Topbar shows the project name + address + current phase. All three come
// from the API now — the old hardcoded "Nieuwbouw Bertem / De Vos–Peeters /
// Ruwbouw" strings were leftover Phase 1 design data and stayed visible
// even after the user reset their project.
export function Topbar({ right, onMenuClick }: TopbarProps) {
  const project = useProject();
  const dashboard = useDashboard();
  // Clerk-derived display name. UserButton renders the avatar + menu
  // internally, but `useUser` is the canonical way to surface the user's
  // name in our own chrome (e.g. tooltips, accessible labels) so the
  // header stays consistent with whatever the user set in Clerk.
  const { user } = useUser();
  const displayName =
    user?.fullName ??
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ??
    user?.primaryEmailAddress?.emailAddress ??
    'Gebruiker';

  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const projectName = project.data?.name?.trim() || 'Mijn project';
  const projectAddress = project.data?.address?.trim() || '';
  const currentPhaseLabel = dashboard.data?.currentPhase?.label;

  return (
    <div className={styles.topbar}>
      {onMenuClick && (
        <button
          type="button"
          className={styles.menuBtn}
          onClick={onMenuClick}
          aria-label="Open menu"
        >
          <Icon name="menu" size={18} />
        </button>
      )}
      <div className={styles.brand}>
        <div className={styles.logo} aria-hidden="true">
          b
        </div>
        <div className={styles.brandText}>
          <div className={styles.project}>{projectName}</div>
          {projectAddress && <div className={styles.subtitle}>{projectAddress}</div>}
        </div>
      </div>

      <div className={styles.divider} aria-hidden="true" />

      {currentPhaseLabel && <Pill kind="warn">Fase: {currentPhaseLabel}</Pill>}

      <button
        type="button"
        className={styles.search}
        onClick={() => setPaletteOpen(true)}
        aria-label="Zoeken (⌘K)"
      >
        <Icon name="search" size={14} aria-hidden />
        <span className={styles.searchPlaceholder}>Zoek documenten, contacten, mails…</span>
        <kbd className={styles.kbd}>⌘K</kbd>
      </button>

      <div className={styles.right}>
        {right}
        <Btn variant="ghost" aria-label="Meldingen">
          <Icon name="bell" size={16} />
          <span className={styles.bellDot} aria-hidden="true" />
        </Btn>
        <UserButton
          appearance={{
            elements: { avatarBox: { width: 28, height: 28 } },
          }}
        />
        {/* Visually-hidden user name so assistive tech sees the signed-in
            user without needing Clerk's menu to open. */}
        <span className="sr-only">Ingelogd als {displayName}</span>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
